import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X, Loader2, RefreshCw, Power, History } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import ZalozitDodavateleModal from '../ZalozitDodavateleModal';
import { ACCENT, OK, BAD } from '../../lib/uiTokens';

const DPH_OPTS = [{ value: 0, label: '0 %' }, { value: 12, label: '12 %' }, { value: 21, label: '21 %' }];


const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : null);

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

const PRAZDNY = { nazev: '', kategorie_id: '', castka_bez_dph: '', dph_sazba: 21, dodavatel: '', dodavatel_id: '', den_generovani: 1, platnost_od: '', platnost_do: '', poznamka: '', aktivni: true };

/**
 * Správa fixních opakujících se nákladů (šablony). Šablona = nájem/pojištění/
 * předplatné…; scheduler z aktivních šablon 1× měsíčně vytvoří doklad (schvaleny).
 * „Vygenerovat teď" spustí generování pro aktuální měsíc ručně (idempotentní).
 *
 * Běží uvnitř bočního inspektoru (SidePanel dodává rám + titulek + zavření).
 * Pouze CEO (endpointy /finance/sablony vyžadují superadmina).
 */
export default function SablonyPanel({ cardStyle, isDark, token, onChange }) {
    const [sablony, setSablony] = useState([]);
    const [kategorie, setKategorie] = useState([]);
    const [dodavatele, setDodavatele] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);      // null = formulář zavřený
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [generuji, setGeneruji] = useState(false);
    const [aresOpen, setAresOpen] = useState(false);

    const dnes = new Date();

    const nactiDodavatele = useCallback(() => {
        fetch('/api/dodavatele?aktivni=1').then(r => (r.ok ? r.json() : []))
            .then(d => setDodavatele(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            fetch('/api/finance/sablony').then(r => (r.ok ? r.json() : [])),
            fetch('/api/finance/kategorie').then(r => (r.ok ? r.json() : [])),
        ]).then(([s, k]) => {
            setSablony(Array.isArray(s) ? s : []);
            setKategorie((Array.isArray(k) ? k : []).filter(c => c.skupina !== 'trzby'));
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);
    useEffect(() => { load(); nactiDodavatele(); }, [load, nactiDodavatele]);

    // Popisek kategorie: u subúčtu „Účet › Subúčet"
    const katMap = Object.fromEntries(kategorie.map(k => [k.id, k]));
    const katLabel = (id) => {
        const k = katMap[id]; if (!k) return '—';
        const p = k.parent_id ? katMap[k.parent_id] : null;
        return p ? `${p.nazev} › ${k.nazev}` : k.nazev;
    };
    // Do dropdownu jen listy (účtovatelné) — účet bez potomků nebo subúčet
    const parentIds = new Set(kategorie.filter(k => k.parent_id).map(k => k.parent_id));
    const listy = kategorie.filter(k => !parentIds.has(k.id));

    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    const zacitNovy = () => { setEditId(null); setForm({ ...PRAZDNY }); };
    const zacitEdit = (s) => {
        setEditId(s.id);
        setForm({
            nazev: s.nazev, kategorie_id: s.kategorie_id, castka_bez_dph: s.castka_bez_dph,
            dph_sazba: s.dph_sazba, dodavatel: s.dodavatel || '', dodavatel_id: s.dodavatel_id || '',
            den_generovani: s.den_generovani,
            platnost_od: s.platnost_od ? String(s.platnost_od).slice(0, 10) : '',
            platnost_do: s.platnost_do ? String(s.platnost_do).slice(0, 10) : '',
            poznamka: s.poznamka || '', aktivni: !!s.aktivni,
        });
    };

    const ulozit = async () => {
        if (!form.nazev.trim()) { alert('Zadej název.'); return; }
        if (!form.kategorie_id) { alert('Vyber kategorii.'); return; }
        setSaving(true);
        const url = editId ? `/api/finance/sablony/${editId}` : '/api/finance/sablony';
        const r = await fetch(url, {
            method: editId ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({
                ...form,
                kategorie_id: Number(form.kategorie_id),
                castka_bez_dph: parseFloat(form.castka_bez_dph) || 0,
                dph_sazba: Number(form.dph_sazba),
                den_generovani: Number(form.den_generovani),
                dodavatel_id: form.dodavatel_id || null,
                // název dodavatele pro zobrazení/legacy odvodíme z vybraného katalogu
                dodavatel: dodavatele.find(d => d.id === Number(form.dodavatel_id))?.nazev || form.dodavatel || null,
                platnost_od: form.platnost_od || null,
                platnost_do: form.platnost_do || null,
            }),
        });
        setSaving(false);
        if (r.ok) { setForm(null); setEditId(null); load(); }
        else { const e = await r.json().catch(() => ({})); alert(e.message || 'Uložení selhalo'); }
    };

    const prepnoutAktivni = async (s) => {
        const r = await fetch(`/api/finance/sablony/${s.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ aktivni: !s.aktivni }),
        });
        if (r.ok) load();
    };

    const smazat = async (s) => {
        if (!confirm(`Smazat šablonu "${s.nazev}"?`)) return;
        const r = await fetch(`/api/finance/sablony/${s.id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': token } });
        if (r.ok) load();
    };

    const generovat = async () => {
        setGeneruji(true);
        const r = await fetch('/api/finance/sablony/generovat', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ rok: dnes.getFullYear(), mesic: dnes.getMonth() + 1 }),
        });
        setGeneruji(false);
        const d = await r.json().catch(() => ({}));
        if (r.ok) { alert(`Vygenerováno ${d.vygenerovano ?? 0} dokladů pro ${MESICE[dnes.getMonth()]} ${dnes.getFullYear()}.`); onChange?.(); }
        else alert(d.error || d.message || 'Generování selhalo');
    };

    const doplnitZpetne = async () => {
        if (!confirm('Zpětně doplnit chybějící doklady ze šablon za celé období jejich platnosti (do teď)?')) return;
        setGeneruji(true);
        const r = await fetch('/api/finance/sablony/generovat-zpetne', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
        });
        setGeneruji(false);
        const d = await r.json().catch(() => ({}));
        if (r.ok) { alert(`Zpětně vygenerováno ${d.vygenerovano ?? 0} dokladů.`); onChange?.(); }
        else alert(d.error || d.message || 'Zpětné doplnění selhalo');
    };

    const inp = 'rounded-lg px-2 py-1.5 text-sm w-full';
    const inpStyle = { ...cardStyle, color: 'var(--text-primary)' };

    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} /></div>;

    return (
        <div className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Fixní náklady se generují automaticky 1× měsíčně z aktivních šablon.
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={doplnitZpetne} disabled={generuji}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ ...cardStyle, color: 'var(--text-secondary)', opacity: generuji ? 0.6 : 1 }}
                        title="Zpětně doplnit chybějící doklady za období platnosti šablon">
                        <History size={13} /> Doplnit zpětně
                    </button>
                    <button onClick={generovat} disabled={generuji}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ ...cardStyle, color: 'var(--text-secondary)', opacity: generuji ? 0.6 : 1 }}
                        title="Vytvořit doklady ze šablon pro aktuální měsíc">
                        <RefreshCw size={13} className={generuji ? 'animate-spin' : ''} /> Vygenerovat teď
                    </button>
                </div>
            </div>

            {/* Seznam šablon */}
            <div className="space-y-2">
                {sablony.length === 0 && (
                    <div className="text-center py-6 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        Zatím žádné šablony fixních nákladů.
                    </div>
                )}
                {sablony.map(s => (
                    <div key={s.id} className="rounded-xl p-3 flex items-center gap-3" style={{ ...cardStyle, opacity: s.aktivni ? 1 : 0.55 }}>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev}</div>
                            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                                {katLabel(s.kategorie_id)} · {fmt(s.castka_bez_dph)} Kč + {s.dph_sazba} % · {s.den_generovani}. den
                                {(s.dodavatel_katalog?.nazev || s.dodavatel) ? ` · ${s.dodavatel_katalog?.nazev || s.dodavatel}` : ''}
                            </div>
                            {(s.platnost_od || s.platnost_do) && (
                                <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                                    Platnost: {fmtDate(s.platnost_od) || '…'} – {fmtDate(s.platnost_do) || '…'}
                                </div>
                            )}
                        </div>
                        <button onClick={() => prepnoutAktivni(s)} title={s.aktivni ? 'Vypnout' : 'Zapnout'}
                            className="p-1.5 rounded-lg flex-shrink-0" style={{ color: s.aktivni ? OK : 'var(--text-tertiary)' }}>
                            <Power size={14} />
                        </button>
                        <button onClick={() => zacitEdit(s)} title="Upravit" className="p-1.5 rounded-lg flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                            <Pencil size={14} />
                        </button>
                        <button onClick={() => smazat(s)} title="Smazat" className="p-1.5 rounded-lg flex-shrink-0" style={{ color: BAD }}>
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>

            {/* Formulář nové / editace */}
            {form ? (
                <div className="rounded-xl p-3 space-y-2" style={{ ...cardStyle, border: `1px solid ${ACCENT}55` }}>
                    <div className="text-xs font-semibold" style={{ color: ACCENT }}>{editId ? 'Upravit šablonu' : 'Nová šablona'}</div>
                    <input className={inp} style={inpStyle} placeholder="Název (např. Nájem provozovny)" value={form.nazev} onChange={e => set({ nazev: e.target.value })} />
                    <Dropdown value={form.kategorie_id} onChange={v => set({ kategorie_id: v })}
                        options={[{ value: '', label: '— P&L kategorie —' }, ...listy.map(k => ({ value: k.id, label: katLabel(k.id) }))]}
                        placeholder="— P&L kategorie —" isDark={isDark} accent={ACCENT} height={34} />
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Částka bez DPH</div>
                            <input type="number" step="0.01" className={inp} style={inpStyle} value={form.castka_bez_dph} onChange={e => set({ castka_bez_dph: e.target.value })} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>DPH</div>
                            <Dropdown value={form.dph_sazba} onChange={v => set({ dph_sazba: Number(v) })}
                                options={DPH_OPTS} isDark={isDark} accent={ACCENT} height={34} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Den v měsíci</div>
                            <input type="number" min="1" max="28" className={inp} style={inpStyle} value={form.den_generovani} onChange={e => set({ den_generovani: e.target.value })} />
                        </div>
                    </div>
                    {/* Dodavatel — výběr z katalogu nebo založení nového */}
                    <div>
                        <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Dodavatel</div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                                <Dropdown value={form.dodavatel_id || ''} onChange={v => set({ dodavatel_id: v })}
                                    options={[{ value: '', label: '— bez dodavatele —' }, ...dodavatele.map(d => ({ value: d.id, label: d.ico ? `${d.nazev} (${d.ico})` : d.nazev }))]}
                                    placeholder="— vyber dodavatele —" isDark={isDark} accent={ACCENT} height={34} />
                            </div>
                            <button onClick={() => setAresOpen(true)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
                                style={{ background: `${ACCENT}14`, color: ACCENT, border: `1px solid ${ACCENT}55` }}
                                title="Založit nového dodavatele (ARES)">
                                <Plus size={13} /> Nový
                            </button>
                        </div>
                    </div>

                    {/* Platnost od–do (nepovinné) */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Platnost od</div>
                            <input type="date" className={inp} style={inpStyle} value={form.platnost_od} onChange={e => set({ platnost_od: e.target.value })} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Platnost do</div>
                            <input type="date" className={inp} style={inpStyle} value={form.platnost_do} onChange={e => set({ platnost_do: e.target.value })} />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <input type="checkbox" checked={form.aktivni} onChange={e => set({ aktivni: e.target.checked })} /> Aktivní
                        </label>
                        <div className="flex gap-2">
                            <button onClick={ulozit} disabled={saving}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                                style={{ background: ACCENT, opacity: saving ? 0.5 : 1 }}>
                                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Uložit
                            </button>
                            <button onClick={() => { setForm(null); setEditId(null); }} className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--text-secondary)' }}>Zrušit</button>
                        </div>
                    </div>
                </div>
            ) : (
                <button onClick={zacitNovy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: `${ACCENT}14`, color: ACCENT, border: `1px solid ${ACCENT}55` }}>
                    <Plus size={15} /> Přidat šablonu
                </button>
            )}

            {/* Založení nového dodavatele (ARES) — standalone, vrátí založeného */}
            <ZalozitDodavateleModal
                open={aresOpen}
                onClose={() => setAresOpen(false)}
                onSuccess={(dod) => {
                    nactiDodavatele();
                    if (dod?.id) setForm(f => (f ? { ...f, dodavatel_id: dod.id, dodavatel: dod.nazev } : f));
                    setAresOpen(false);
                }}
                cardStyle={cardStyle}
                isDark={isDark}
            />
        </div>
    );
}
